import { usarDataHora } from "@/composable/UsarDataHora/usarDataHora"
import { usarLeitorDeDados } from "@/composable/UsarLeitorDeDados/usarLeitorDeDados"
import {
  AtividadePlanejada,
  ParametrosAtividadePlanejada,
} from "@/repositorios/eventos/eventos.modelo"
import AtividadesPlanejadasRepositorio from "@/repositorios/eventos/eventos.repositorio"
import { defineStore } from "pinia"
import { ModeloEventoCalendario } from ".."

interface EventoPlanejadoStore {
  eventosPlanejados: AtividadePlanejada[]
  periodoDeFiltragem: {
    dataInicio: Date;
    dataFim: Date;
  }
}

const atividadesPlanejadas = new AtividadesPlanejadasRepositorio({ utilizaCache: true })

const { converterParaTimeZoneLocal,
  hoje,
  pegarSomenteData,
  pegarDataInicioEFimDaSemana,
  dataEstaEntrePeriodo,
  adicionarUmDia,
  subtrairUmDia
} = usarDataHora()

const { leitor } = usarLeitorDeDados()

const lerEventosPlanejados = leitor<AtividadePlanejada, ParametrosAtividadePlanejada>(
  atividadesPlanejadas.listarAtividadesPlanejadasFrontend,
  {}
)

export const usarEventosPlanejadosStore = defineStore(
  "usarEventosPlanejadosStore",
  {
    state: (): EventoPlanejadoStore => ({
      eventosPlanejados: [],
      periodoDeFiltragem: {
        dataInicio: hoje,
        dataFim: pegarDataInicioEFimDaSemana(hoje).fimDaSemana
      }
    }),
    getters: {
      statusEventoPlanejados: () => lerEventosPlanejados.resultado.status,
    },
    actions: {
      mapearDTOsparaModelo(evento: AtividadePlanejada): ModeloEventoCalendario {
        const inicio = converterParaTimeZoneLocal(
          evento.dataInicio,
          evento.horaInicio
        )
        const fim = converterParaTimeZoneLocal(evento.dataFim, evento.horaFim)
        return {
          id: evento.id,
          start: inicio,
          end: fim,
          atividade: {
            codigo: evento.atividadePlanejada.codigo,
            id: evento.atividadePlanejada.id,
          },
          duracao: evento.duracaoEmMinutos,
          profissionais: evento.profissionaisAlocados,
          status: "planejado",
          tipoDeAtividade: evento.atividadePlanejada.tipoDeAtividade,
          modoDeRegistroInicio: null,
          modoDeRegistroFim: null,
          problemas: null,
          projeto: evento.atividadePlanejada.projeto,
          calendario: evento.calendarioQueCriouId,
          faseDaAtividade: "naoPodeIniciar",
        }
      },
      async verEventosPlanejados(
        parametros: ParametrosAtividadePlanejada = {
          "por-pagina": "1000",
        },
        utilizarCache = true
      ) {
        const definirCacheNoLeitor = () => {
          lerEventosPlanejados.sobrescreverCabecalhos({
            "X-Timesheet-Cache-Ignore": !utilizarCache,
          })
        }

        const dataInicioSelecionada = this.periodoDeFiltragem.dataInicio
        const definirDataDeInicioDeFiltragem = () => {
          if (dataInicioSelecionada > hoje) {
            return dataInicioSelecionada
          }

          return hoje
        }

        const { dataFim } = this.periodoDeFiltragem
        const dataInicio = definirDataDeInicioDeFiltragem()

        const intervaloDoCalendarioEstaNoPresente = dataEstaEntrePeriodo(hoje, dataInicioSelecionada, dataFim)
        const intervaloDoCalendarioEstaNoPassado = dataFim < dataInicio
        const intervaloDoCalendarioEstaNoFuturo = !intervaloDoCalendarioEstaNoPresente && !intervaloDoCalendarioEstaNoPassado

        definirCacheNoLeitor()

        if (intervaloDoCalendarioEstaNoPassado) {
          lerEventosPlanejados.sobrescreverParametros({
            ...parametros,
            ja_foram_realizados: "igual.false",
            data_inicio: `intercede.(data_fim,${pegarSomenteData(subtrairUmDia(dataInicioSelecionada))},${pegarSomenteData(adicionarUmDia(dataFim))})`
          })
        } else if (intervaloDoCalendarioEstaNoPresente) {
          lerEventosPlanejados.sobrescreverParametros({
            ...parametros,
            data_inicio: `intercede.(data_fim,${pegarSomenteData(subtrairUmDia(hoje))},${pegarSomenteData(adicionarUmDia(dataFim))})`
          })
        } else if (intervaloDoCalendarioEstaNoFuturo) {
          lerEventosPlanejados.sobrescreverParametros({
            ...parametros,
            data_inicio: `intercede.(data_fim,${pegarSomenteData(subtrairUmDia(dataInicioSelecionada))},${pegarSomenteData(adicionarUmDia(dataFim))})`
          })
        }

        await lerEventosPlanejados.executar()

        const estadoDaRequisicao = lerEventosPlanejados.resultado.status

        if (estadoDaRequisicao === "sucesso") {
          this.eventosPlanejados = lerEventosPlanejados.resultado.dados
        } else if (estadoDaRequisicao === "vazio") {
          this.eventosPlanejados = []
        }
      },
      limparEventos() {
        this.eventosPlanejados = []
      },
    },
  }
)
